import { useCallback, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * Returns a play function that attempts to play the given audio element.
 * If the browser blocks autoplay (NotAllowedError), a toast is shown prompting
 * the user to interact with the page. The toast auto-dismisses on the next
 * user interaction (click), since that interaction unblocks autoplay.
 * Other errors (e.g. missing audio file) are ignored silently.
 */
export const useAudioWithAutoplayHandling = () => {
  const { t } = useTranslation("calling");
  const toastIdRef = useRef<string | number | null>(null);

  const playAudio = useCallback(
    (audio: HTMLAudioElement | null) => {
      if (!audio) return;

      audio.currentTime = 0;
      const playPromise = audio.play();
      playPromise.catch((error: unknown) => {
        if (!(error instanceof DOMException) || error.name !== "NotAllowedError") return;

        if (toastIdRef.current !== null) return;

        const dismissOnInteraction = () => {
          if (toastIdRef.current !== null) {
            toast.dismiss(toastIdRef.current);
            toastIdRef.current = null;
          }
          document.removeEventListener("click", dismissOnInteraction);
        };

        const id = toast.warning(
          t(
            "Sound is blocked by your browser. Click anywhere on the page to enable sound — it will play on your next notification."
          ),
          {
            position: "top-right",
            duration: Infinity,
            closeButton: true,
            onDismiss: () => {
              toastIdRef.current = null;
              document.removeEventListener("click", dismissOnInteraction);
            },
          }
        );

        toastIdRef.current = id;
        document.addEventListener("click", dismissOnInteraction);
      });
    },
    [t]
  );

  return { playAudio };
};
